import { Reveal } from "./Reveal";

const chapters = [
  {
    number: "01",
    title: "The Name",
    body: "TABI Essence of Strength was created to honour a lasting legacy. A name carried with love, distilled into something eternal, a fragrance house founded on memory, gratitude, and grace.",
    dedication: "Named in loving memory of Tabi, a father taken too soon, a kind, funny and cheeky soul whose warmth lives on in every essence we create.",
    image: "/images/tabi-lion.png",
    alt: "Golden lion wearing a crown above the TABI wordmark",
  },
  {
    number: "02",
    title: "The Craft",
    body: "We compose with patience and intention. Rare ingredients, balanced with restraint, become scents that feel less like perfume and more like remembrance, quiet, powerful, unmistakably yours.",
    image: "/images/craft-perfumes.png",
    alt: "TABI Essence of Strength perfume bottles, Guardian and Legacy, in black and gold",
  },
  {
    number: "03",
    title: "The Promise",
    body: "Honouring the past, inspiring the future. Every essence we create carries a story forward, so that what is loved is never lost, only transformed.",
    image: "/images/promise-king.png",
    alt: "The crowned lion of TABI Essence of Strength overlooking a golden sunset",
    square: true,
  },
];

export default function Manifesto() {
  return (
    <section id="story" data-testid="manifesto-section" className="relative px-6 py-24 md:px-10 md:py-40">
      <div className="mx-auto max-w-7xl">
        <Reveal className="mb-20 md:mb-32 md:flex md:items-end md:justify-between">
          <div>
            <p data-testid="manifesto-eyebrow" className="mb-6 font-mono text-[11px] uppercase tracking-[0.5em] text-gold">
              Our Story
            </p>
            <h2 data-testid="manifesto-heading" className="font-serif text-4xl font-light leading-tight text-neutral-100 sm:text-5xl lg:text-6xl">
              Meaning, <em className="text-gold-light">in every note</em>
            </h2>
          </div>
          <p className="mt-8 max-w-sm text-base font-light leading-relaxed text-neutral-400 md:mt-0">
            Three quiet chapters on why TABI Essence exists — and the legacy it carries into the world.
          </p>
        </Reveal>

        <div className="space-y-28 md:space-y-40">
          {chapters.map((c, i) => (
            <div
              key={c.number}
              data-testid={`manifesto-chapter-${c.number}`}
              className={`grid items-center gap-10 md:grid-cols-12 md:gap-16 ${
                i % 2 === 1 ? "" : ""
              }`}
            >
              <Reveal
                className={`md:col-span-5 ${i % 2 === 1 ? "md:order-2 md:col-start-8" : ""}`}
                delay={0.1}
              >
                <div className="group relative overflow-hidden border border-white/10">
                  <img
                    src={c.image}
                    alt={c.alt}
                    className={`${c.square ? "aspect-square" : "aspect-[3/4]"} w-full object-cover transition-transform duration-[1600ms] ease-out group-hover:scale-105`}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
                  <span className="absolute bottom-5 left-5 font-mono text-xs tracking-[0.4em] text-gold-light/80">
                    {c.number}- TABI
                  </span>
                </div>
              </Reveal>
              <Reveal
                className={`md:col-span-6 ${i % 2 === 1 ? "md:order-1 md:col-start-1" : "md:col-start-7"}`}
                delay={0.25}
              >
                <span className="font-mono text-6xl font-light text-gold/30 md:text-8xl">{c.number}</span>
                <h3 className="mt-6 font-serif text-2xl font-light text-neutral-100 sm:text-3xl lg:text-4xl">
                  {c.title}
                </h3>
                <div className="gold-rule mt-8 w-24 opacity-70" />
                <p className="mt-8 max-w-md text-base font-light leading-relaxed text-neutral-400 sm:text-lg">
                  {c.body}
                </p>
                {c.dedication && (
                  <p data-testid="manifesto-dedication" className="mt-6 max-w-md border-l border-gold/40 pl-5 font-serif text-lg font-light italic leading-relaxed text-gold-light/90">
                    {c.dedication}
                  </p>
                )}
              </Reveal>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
